<script lang="ts" setup>
import { type OtSelectVariant } from '@1trading/design-system';
import { computed, type PropType } from 'vue';

import { authService } from '@exchange/libs/auth/service/src';
import { settingsService } from '@exchange/libs/settings/service/src';
import { langsInfoService, type LanguageListItem } from '@exchange/libs/utils/langs-info/src';

const { language, languageList } = langsInfoService;

defineProps({
  toggle: { type: Boolean, default: false },
  variant: { type: String as PropType<OtSelectVariant>, default: 'dark-borderless' },
});
const emit = defineEmits<{
  (e: 'language-changed'): void;
}>();

const uniqueId = `language-select-${crypto.randomUUID()}`;

const chosenLanguage = computed(() => languageList.find((item) => item.id === language.value));

const setLanguageSetting = async (item: LanguageListItem) => {
  const languageCode = item.id;

  await langsInfoService.setLanguage(languageCode);
  emit('language-changed');

  if (authService.hasBeenAuthenticated) {
    await settingsService.setSettings({
      path: 'language',
      value: languageCode,
    });
  }
};
</script>

<template>
  <ot-select
    v-if="!toggle"
    v-model:value="chosenLanguage"
    class="language-setting"
    label="name"
    :variant="variant"
    :options="languageList"
    testid="language-select"
    @update:value="setLanguageSetting"
  >
    <template #selected-option="{ name }">
      <x-list-item
        class="language-setting__selected"
        :title="name"
        left-icon-name="global"
      />
    </template>
    <template #option="{ name, icon, id }">
      <x-list-item
        :active="chosenLanguage && chosenLanguage.id === id"
        :title="name"
        :left-icon-name="icon"
        left-icon-variant="flag"
      />
    </template>
  </ot-select>
  <ot-button-dropdown
    v-else
    class="language-setting language-setting--toggle"
    variant="text"
    size="sm"
    testid="language-select"
    :unique-id="uniqueId"
    :btn-text="chosenLanguage?.id.toLocaleUpperCase()"
  >
    <template #options>
      <x-list-item
        v-for="item in languageList"
        :key="item.id"
        :active="chosenLanguage && chosenLanguage.id === item.id"
        :title="item.name"
        :left-icon-name="item.icon"
        :testid="`language-select-${item.id}`"
        left-icon-variant="flag"
        @click="setLanguageSetting(item)"
      />
    </template>
  </ot-button-dropdown>
</template>

<style lang="scss">
.language-setting {
  & &__selected {
    --ot-list-item-padding-vertical-custom: 0;
    --ot-list-item-padding-horizontal-custom: 0;

    path {
      fill: currentcolor;
    }
  }
}

.language-setting--toggle {
  &.language-setting {
    button:not(.ot-button--primary) {
      background-color: rgb(var(--v-theme-elevation-2));
    }
  }
}
</style>
